import { orange } from '@mui/material/colors'
import type { SxProps, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { useMemo } from 'react'

import { useIsFieldHighlighted } from '@/components/UberForm'

/** Distinct orange for view-history diff (theme `warning` defaults to amber). */
const HISTORY_HIGHLIGHT = orange[600]
const HISTORY_FILL = alpha(HISTORY_HIGHLIGHT, 0.12)

const fieldHighlightSx = (highlighted: boolean): SxProps<Theme> =>
	highlighted
		? {
				'& .MuiOutlinedInput-notchedOutline': {
					borderColor: HISTORY_HIGHLIGHT,
					borderWidth: 2,
				},
				// Read-only history uses `disabled` — MUI otherwise paints grey outline / #fafafa fill
				'& .MuiOutlinedInput-root.Mui-disabled': {
					opacity: 1,
					backgroundColor: `${HISTORY_FILL} !important`,
				},
				'& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
					borderColor: `${HISTORY_HIGHLIGHT} !important`,
					borderWidth: 2,
				},
				'& .MuiOutlinedInput-root.Mui-disabled input': {
					WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
				},
				'& .MuiOutlinedInput-root.Mui-disabled .MuiSelect-select': {
					WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
				},
				bgcolor: HISTORY_FILL,
			}
		: {}

const boxHighlightSx = (highlighted: boolean): SxProps<Theme> =>
	highlighted
		? {
				border: 2,
				borderColor: HISTORY_HIGHLIGHT,
				borderStyle: 'solid',
				borderRadius: 1,
				bgcolor: alpha(HISTORY_HIGHLIGHT, 0.1),
				boxSizing: 'border-box',
			}
		: {}

/** MUI field outline + subtle fill when value differs from previous history version (form highlights). */
export function useViewFieldHighlightSx(
	fieldName: string,
	value: unknown,
): SxProps<Theme> {
	const highlighted = useIsFieldHighlighted(fieldName, value)
	return useMemo(() => fieldHighlightSx(highlighted), [highlighted])
}

/** Border + fill for non-TextField blocks (e.g. Monaco description). */
export function useViewFieldBoxHighlightSx(
	fieldName: string,
	value: unknown,
): SxProps<Theme> {
	const highlighted = useIsFieldHighlighted(fieldName, value)
	return useMemo(() => boxHighlightSx(highlighted), [highlighted])
}
